'use client';

import { useEffect, useState } from 'react';
import { SearchParams } from '@/constants/listing-search-keys';
import { useFavorites } from '@/context/fav-provider';
import { useSearch } from '@/context/listing-search-provider';
import getFavorites from '@/services/get-favorites';
import {
  parseGuestCountsFromListingQuery,
  totalGuestsFromCounts,
} from '@/utils/guest-search-params';
import {
  Listing,
  ListingLos,
} from '@/utils/platform-api/__generated__/graphql';
import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import clsx from 'clsx';
import { BrandConfig } from 'types/brand-config';

import Alert from '@/components/ui/alert';
import ListingCard from '@/components/listing/listing-card';
import { ListingCardSkeleton } from '@/components/listing/listing-card-skeleton';
import DateSearch from '@/components/search/date-search';

const cardImageSizes = [
  { viewport: 640, image: 640 },
  { viewport: 1600, image: 480 },
];

const Favorites = ({
  brand,
  horizontal,
}: {
  brand: BrandConfig;
  horizontal?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const {
    searchParams,
    applySearchParams,
    selectedDates,
    clearDateParams,
    setParam,
  } = useSearch();
  const { favoritesIds, favoritesCount, removeFromFavorites } = useFavorites();
  const [favorites, setFavorites] = useState<Listing[]>([]);
  const [rates, setRates] = useState<ListingLos[]>([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [errorFavorites, setErrorFavorites] = useState<string | null>(null);

  const guestCounts = parseGuestCountsFromListingQuery({
    adults: searchParams.get(SearchParams.ADULTS) ?? undefined,
    children: searchParams.get(SearchParams.CHILDREN) ?? undefined,
    guests: searchParams.get(SearchParams.GUESTS) ?? undefined,
  });
  const arriveParam = searchParams.get(SearchParams.ARRIVE);
  const departParam = searchParams.get(SearchParams.DEPART);
  const totalGuestsParam = totalGuestsFromCounts(guestCounts).toString();

  useEffect(() => {
    if (!favoritesIds.length) {
      setFavorites((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    setLoadingFavorites(true);
    getFavorites(favoritesIds, arriveParam, departParam, totalGuestsParam)
      .then(({ data }) => {
        setFavorites(data.listings.listings as Listing[]);
        setRates(data.listings.rates as ListingLos[]);
      })
      .catch(() => {
        setErrorFavorites('There was a problem loading your favorites.');
      })
      .finally(() => {
        setLoadingFavorites(false);
      });
  }, [arriveParam, departParam, totalGuestsParam, open, favoritesIds]);

  const hasHidddenFavorites =
    favorites.length < favoritesCount && !loadingFavorites;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={clsx(
          horizontal ? 'flex-row-reverse gap-[0.5rem]' : 'flex-col',
          'flex items-center justify-center gap-0 p-0 text-primary-main'
        )}
      >
        <div>
          {!horizontal && (
            <svg
              className="h-7 w-7"
              viewBox="0 0 28 26"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="icon=heart">
                <path
                  id="Vector"
                  d="M25.549 2.14507C24.1219 0.683523 22.2288 -0.0762014 20.2206 0.00604151C18.0437 0.0952161 15.8622 1.16533 13.898 3.10512C12.5546 1.77791 11.123 0.861854 9.63699 0.381234C8.22797 -0.0750749 6.77599 -0.122541 5.43703 0.244576C2.94918 0.929024 0.971309 3.00321 0.275947 5.66123C-0.576697 8.9213 0.567698 12.4639 3.41637 15.3813L13.7035 25.9178C13.7555 25.9711 13.8245 26 13.898 26C13.9704 26 14.0405 25.9699 14.0925 25.9178L24.3808 15.3813C26.5226 13.1878 27.701 10.6735 27.7881 8.11162C27.865 5.85444 27.0486 3.67957 25.5502 2.145L25.549 2.14507Z"
                  fill="currentColor"
                />
              </g>
            </svg>
          )}
          {horizontal && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1.75em"
              height="1.75em"
              viewBox="0 0 24 24"
            >
              <path
                fill="currentColor"
                d="m12.1 18.55l-.1.1l-.11-.1C7.14 14.24 4 11.39 4 8.5C4 6.5 5.5 5 7.5 5c1.54 0 3.04 1 3.57 2.36h1.86C13.46 6 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5c0 2.89-3.14 5.74-7.9 10.05M16.5 3c-1.74 0-3.41.81-4.5 2.08C10.91 3.81 9.24 3 7.5 3C4.42 3 2 5.41 2 8.5c0 3.77 3.4 6.86 8.55 11.53L12 21.35l1.45-1.32C18.6 15.36 22 12.27 22 8.5C22 5.41 19.58 3 16.5 3"
              />
            </svg>
          )}
          {favoritesCount > 0 && (
            <div
              className={clsx(
                'absolute -top-2 right-2 flex h-[1.25rem] w-[1.25rem] items-center justify-center rounded-full bg-secondary-main !text-center'
              )}
            >
              <span className="text-center text-xs text-white drop-shadow-md">
                {favoritesCount}
              </span>
            </div>
          )}
        </div>

        {!horizontal && (
          <>
            <span className="whitespace-nowrap text-sm font-semibold tracking-[0.05em] text-inherit xl:text-[15px]">
              Favorites
            </span>
            {/* <span className="ut-xxs block whitespace-nowrap text-inherit sm:hidden">
              Favorites
            </span> */}
          </>
        )}
        {horizontal && (
          <span className="caps-md whitespace-nowrap font-semibold text-inherit ">
            Favorites
          </span>
        )}
      </button>
      <Dialog
        open={open}
        onClose={setOpen} // Close dialog on backdrop click or escape key
        className="relative"
      >
        <DialogBackdrop
          as="div"
          transition
          className="fixed inset-0 z-20 bg-gray-500 bg-opacity-75 transition-opacity ease-in-out data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:max-lg:duration-200 data-[leave]:max-lg:ease-in data-[enter]:lg:duration-500"
        />

        <div className="fixed inset-0 right-0 z-20 flex w-screen md:justify-end">
          <DialogPanel
            onClick={(e) => {
              e.stopPropagation();
            }}
            transition
            className={clsx(
              'no-scrollbar relative h-screen w-full transform overflow-y-scroll rounded-t-xl bg-white text-left shadow-xl',
              'md:max-w-lg md:overflow-visible md:rounded-t-none',
              'transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in'
            )}
          >
            <div className="sticky top-0 z-50 flex flex-col gap-[1rem] bg-white px-[1rem] py-[1rem]">
              <div className="flex flex-row items-center justify-between">
                <DialogTitle className="h5 font-semibold !text-primary-main">
                  Favorites {favorites.length ? `(${favorites.length})` : ''}
                </DialogTitle>
                <CloseButton
                  aria-label="Close"
                  type="button"
                  className="p-0 text-gray-5"
                  onClick={() => setOpen(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 21 21"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M20.092 0.292893C19.7015 -0.0976309 19.0683 -0.0976312 18.6778 0.292893L10.1924 8.77829L1.70711 0.292975C1.31658 -0.0975498 0.683418 -0.0975499 0.292893 0.292974C-0.0976309 0.683499 -0.0976313 1.31666 0.292893 1.70719L8.77821 10.1925L0.293043 18.6777C-0.0974814 19.0682 -0.0974814 19.7014 0.293043 20.0919C0.683567 20.4824 1.31673 20.4824 1.70726 20.0919L10.1924 11.6067L18.6777 20.092C19.0682 20.4825 19.7014 20.4825 20.0919 20.092C20.4824 19.7014 20.4824 19.0683 20.0919 18.6778L11.6066 10.1925L20.092 1.70711C20.4826 1.31658 20.4826 0.683418 20.092 0.292893Z"
                      fill="#BEBEBE"
                    />
                  </svg>
                </CloseButton>
              </div>
              <div className="relative flex flex-col gap-[1rem]">
                <div className="hidden h-fit w-full lg:block">
                  <DateSearch
                    brand={brand}
                    id="enter-filter-dates"
                    label="Travel Dates"
                    name="dates"
                    direction="horizontal"
                    numberOfMonths={2}
                    placeholder="Travel Dates"
                    pickerClassname="!origin-bottom-left !-left-1/2"
                    radius="square"
                    shouldSubmit
                    style="gray"
                    hideFlex
                    applySearchParams={applySearchParams}
                    selectedDates={selectedDates}
                    clearDateParams={clearDateParams}
                    setDateParam={setParam}
                  />
                </div>
                <div className="lg:hidden">
                  <DateSearch
                    brand={brand}
                    id="enter-filter-dates"
                    label="Travel Dates"
                    modal
                    name="dates"
                    direction="vertical"
                    numberOfMonths={12}
                    placeholder="Travel Dates"
                    radius="square"
                    shouldSubmit
                    style="gray"
                    hideFlex
                    pickerClassname="!origin-center !w-full z-30 fixed top-0 left-0"
                    applySearchParams={applySearchParams}
                    selectedDates={selectedDates}
                    clearDateParams={clearDateParams}
                    setDateParam={setParam}
                  />
                </div>
                {hasHidddenFavorites && !errorFavorites && (
                  <Alert
                    message="Some of your favorites are not available for the dates selected"
                    type="info"
                  />
                )}
              </div>
            </div>
            {loadingFavorites && (
              <div
                aria-busy="true"
                aria-label="Loading favorites"
                className="no-scrollbar grid h-[85dvh] grid-cols-1 gap-[1rem] !overflow-y-scroll px-[1rem] pb-[2rem] sm:grid-cols-2 md:grid-cols-1"
              >
                {Array.from({
                  length: Math.min(Math.max(favoritesCount, 1), 6),
                }).map((_, index) => (
                  <ListingCardSkeleton
                    className="h-fit snap-start"
                    key={`favorite-skeleton-${index}`}
                  />
                ))}
              </div>
            )}
            {favorites &&
              !loadingFavorites &&
              !errorFavorites &&
              favorites.length === 0 && (
                <div className="my-10 flex flex-col items-center justify-center gap-0 px-[1rem] text-center">
                  <div className="mb-2 text-primary-main">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-[5rem] w-[5rem]"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        fillOpacity="0.25"
                        d="M20 11.5c0 5.068-5.972 8.47-7.596 9.302a.88.88 0 0 1-.808 0C9.972 19.97 4 16.568 4 11.5C4 7 7.876 4 12 4c4.267 0 8 3 8 7.5"
                      />
                      <path
                        fill="currentColor"
                        d="m8.286 11.786l3.457 3.457c.121.121.182.182.257.182s.136-.06.257-.182l3.457-3.457c.748-.748.864-1.92.277-2.8c-.915-1.371-2.954-1.301-3.772.13l-.141.248a.09.09 0 0 1-.156 0l-.141-.247c-.818-1.432-2.857-1.502-3.772-.13c-.587.88-.47 2.051.277 2.799"
                      />
                    </svg>
                  </div>
                  {!hasHidddenFavorites && (
                    <p className="font-semibold">
                      You have no favorites saved at this time.
                    </p>
                  )}
                  {hasHidddenFavorites && (
                    <p className="font-semibold">No available favorites.</p>
                  )}
                  {!hasHidddenFavorites && (
                    <p className="sm flex items-center text-gray-7">
                      To add a favorite to your list click the heart icon.
                    </p>
                  )}
                  {hasHidddenFavorites && (
                    <p className="sm flex items-center text-gray-7">
                      Please change the dates to view all your favorites.
                    </p>
                  )}
                </div>
              )}
            {errorFavorites && !loadingFavorites && (
              <div className="my-10 flex flex-col items-center justify-center gap-0 text-center">
                <div className="my-10 flex flex-col items-center justify-center gap-0 px-[1rem] text-center">
                  <div className="mb-2 animate-pulse text-error-dark">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-[5rem] w-[5rem]"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        fillOpacity="0.25"
                        d="M20 11.5c0 5.068-5.972 8.47-7.596 9.302a.88.88 0 0 1-.808 0C9.972 19.97 4 16.568 4 11.5C4 7 7.876 4 12 4c4.267 0 8 3 8 7.5"
                      />
                      <path
                        fill="currentColor"
                        d="m8.286 11.786l3.457 3.457c.121.121.182.182.257.182s.136-.06.257-.182l3.457-3.457c.748-.748.864-1.92.277-2.8c-.915-1.371-2.954-1.301-3.772.13l-.141.248a.09.09 0 0 1-.156 0l-.141-.247c-.818-1.432-2.857-1.502-3.772-.13c-.587.88-.47 2.051.277 2.799"
                      />
                    </svg>
                  </div>
                  <p className="font-semibold">
                    There was a problem loading your favorites.
                  </p>
                  <p className="sm flex items-center text-gray-7">
                    Please try again later.
                  </p>
                </div>
              </div>
            )}
            {favorites && !loadingFavorites && !errorFavorites && (
              <div
                className={clsx(
                  hasHidddenFavorites ? 'h-[80dvh]' : 'h-[85dvh]',
                  'no-scrollbar grid grid-cols-1 gap-[1rem] !overflow-y-scroll px-[1rem] pb-[2rem] sm:grid-cols-2 md:grid-cols-1'
                )}
              >
                {favorites.map((favorite, index) => {
                  const listing_los = rates?.find((r: any) => {
                    return r.id === favorite.id;
                  });
                  return (
                    <ListingCard
                      onFavoriteClick={() => {
                        removeFromFavorites(favorite.id);
                        setFavorites((prev) =>
                          prev.filter((f) => f.id !== favorite.id)
                        );
                      }}
                      className="h-fit snap-start"
                      id={`listing-card-${favorite.id}`}
                      key={favorite.id + `_favorite_${index}`}
                      listing={favorite}
                      imageSizes={cardImageSizes}
                      listingLos={listing_los as ListingLos}
                      brand={brand}
                      searchParams={{
                        arrive: searchParams.get(SearchParams.ARRIVE) || '',
                        depart: searchParams.get(SearchParams.DEPART) || '',
                        adults: guestCounts.adults.toString(),
                        ...(guestCounts.children > 0
                          ? { children: guestCounts.children.toString() }
                          : {}),
                      }}
                    />
                  );
                })}
              </div>
            )}
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
};

export default Favorites;
